import React, { ReactElement } from 'react';
import styled from 'styled-components';
import config from 'config';
import { AppColors, AppConst } from 'helpers';
import { Div, HorizontalView, VerticalView } from 'vendor-deprecated/Wrapper';
import { TextDisplay } from 'vendor-deprecated/Text';
import { useTranslationText } from 'locale';
import { ICourse } from 'types/types';
import { Image } from 'components/common/vendors/Image';
import { Hr } from 'vendor-deprecated/CommonParts';
import { friendlyURL } from 'helpers/commonUtils';
import { AnimationView } from 'vendor-deprecated/Animation';
import { useAppSetting } from 'helpers/useAppSetting';
import { Information } from './CourseDescription';
import { PaymentButton } from './PaymentButton';
import { FreeButton } from './FreeButton';
import { ShareBarHorizontal } from 'components/common/components/ShareBar';

const CartWrapper = styled.div<{ isMobile: boolean }>`
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 4px;
  ${(props) => {
    if (props.isMobile) {
      return `background:transparent;`;
    } else {
      return `
        min-width: 300px;
        max-width: 500px;
        height: 100%;
        background: ${AppColors.WHITE};
        border-radius: 4px;
        box-shadow: 0 2px 8px 2px rgb(20 23 28 / 15%);
      `;
    }
  }}
`;

const i18key = 'public.courseDetails.card';

function getViewPort(el: HTMLElement | null) {
  if (!el) return { isHidden: false, width: 360 };

  const rect = el.getBoundingClientRect();
  const elemTop = rect.top;
  const isHidden = elemTop < 0;

  return { isHidden, width: rect.width };
}

function getRect(el: HTMLElement | null) {
  if (!el) return {} as DOMRect;

  const rect = el.getBoundingClientRect();

  return rect;
}

export interface AddCardProps {
  id?: string;
  type: 'top' | 'content';
  disabled?: boolean;
  addCourseFreeLoading?: boolean;
  course: ICourse;
  isAdded?: boolean;
  onAddToMyCourse: () => void;
  onAddToCart: () => void;
  onBuyNow: () => void;
  onShowBottom: (element: ReactElement | null) => void;
}

export const Card = React.memo(
  (props: AddCardProps & { opacity: number; added: boolean; setAdded: (added: boolean) => void }) => {
    const { appLayout } = useAppSetting();
    const { translation } = useTranslationText();

    return (
      <Div fullWidth opacity={props.opacity} id={props.id}>
        <CartWrapper isMobile={appLayout.isMobile}>
          <VerticalView fullWidth alignContent={'centerLeft'}>
            {!appLayout.isMobile && (
              <Div fullWidth overflow={'hidden'} alignContent={'topCenter'}>
                <Image
                  src={props.course.thumbnail || '/images/course.png'}
                  objectFit={'cover'}
                  width={'100%'}
                  // height={'180px'}
                />
              </Div>
            )}

            <Div fullWidth id={props.id === 'relative-card' ? 'separator' : undefined} />
            <VerticalView fullWidth alignContent={'topCenter'} padding={appLayout.isMobile ? '0px' : '20px'}>
              <HorizontalView
                fullWidth
                marginTop={appLayout.isMobile ? '0px' : '10px'}
                paddingBottom={'10px'}
                alignContent={'centerLeft'}
              >
                <TextDisplay
                  color={appLayout.isMobile ? AppColors.WHITE : AppColors.RED}
                  size={'30px'}
                  fontWeight={600}
                >
                  {props.course.isFree ? translation(`${i18key}.free`) : `${props.course?.price || ''}円`}
                </TextDisplay>
              </HorizontalView>

              {!props.course.isFree && (
                <PaymentButton
                  course={props.course}
                  disabled={props.disabled}
                  onAddToCart={props.onAddToCart}
                  onBuyNow={props.onBuyNow}
                  added={props.added}
                  setAdded={props.setAdded}
                />
              )}

              {props.course.isFree && (
                <FreeButton
                  disabled={!props.opacity || props.disabled}
                  addCourseFreeLoading={props.addCourseFreeLoading}
                  onAddToMyCourse={props.onAddToMyCourse}
                />
              )}

              {!appLayout.isMobile && <Information type={'dark'} course={props.course} />}

              <Hr />
              <VerticalView fullWidth>
                <ShareBarHorizontal
                  disabled={!props.opacity}
                  shareUrl={encodeURIComponent(
                    config.UI_HOST_URL + `/${friendlyURL(props.course.id, props.course.seoName || 'course')}`,
                  )}
                />
                <HorizontalView height={'100%'} alignContent={'center'} flexWrap={'nowrap'}>
                  <TextDisplay
                    marginLeft={'10px'}
                    size={'11px'}
                    color={appLayout.isMobile ? AppColors.WHITE : AppColors.DARK}
                  >
                    {translation(`${i18key}.share`)}
                  </TextDisplay>
                </HorizontalView>
              </VerticalView>
            </VerticalView>
          </VerticalView>
        </CartWrapper>
      </Div>
    );
  },
);

export const AddToCart = React.memo((props: AddCardProps) => {
  const { appLayout } = useAppSetting();
  const [added, setAdded] = React.useState(false);
  const [showOnBottom, setShowOnBottom] = React.useState(false);

  const [cartViewPort, setCartViewPort] = React.useState<{ isHidden: boolean; width: number }>({
    isHidden: false,
    width: 350,
  });

  const bottomAddToCart = (
    <Div width={`${cartViewPort.width}px`} zIndex={AppConst.zIndex.body + 11}>
      <Card id={'bottom-card'} opacity={showOnBottom ? 1 : 0} {...props} added={added} setAdded={setAdded} />
    </Div>
  );

  React.useEffect(() => {
    const data = localStorage.getItem('cartIds') || '{}';
    try {
      const cart = JSON.parse(data);
      setAdded(Boolean(cart[props.course.id]));
    } catch (error) {}
  }, []);

  React.useEffect(() => {
    const onScroll = () => {
      const separator = document.getElementById('separator');
      const footerRect = getRect(document.getElementById('footer'));
      const addCardRect = getRect(document.getElementById('absolute-card'));
      setCartViewPort(getViewPort(separator));
      setShowOnBottom(footerRect.top < addCardRect.top + addCardRect.height + 20);
    };

    window.addEventListener('scroll', onScroll);

    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    props.onShowBottom(!appLayout.isMobile && showOnBottom ? bottomAddToCart : null);
  }, [showOnBottom]);

  return (
    <Div fullWidth position={appLayout.isMobile ? 'relative' : 'absolute'} width={`${cartViewPort.width}px`}>
      <Card
        id={'relative-card'}
        opacity={!appLayout.isMobile && cartViewPort.isHidden ? 0 : 1}
        {...props}
        added={added}
        setAdded={setAdded}
      />
      {!appLayout.isMobile && cartViewPort.isHidden && (
        <Div position={'fixed'} top={'90px'} zIndex={AppConst.zIndex.body + 10} opacity={showOnBottom ? 0 : 1}>
          <AnimationView duration={400} width={`${cartViewPort.width}px`}>
            <Card id={'absolute-card'} opacity={1} {...props} added={added} setAdded={setAdded} />
          </AnimationView>
        </Div>
      )}
    </Div>
  );
});
